"use client";

import type { ReactNode } from "react";
import type { Locale } from "@/i18n/routing";
import type { CurrencyInfo } from "@/lib/currency-format";
import {
	faCartShopping,
	faMinus,
	faNoteSticky,
	faPlus,
	faTrash,
	faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import { removeCartLineAction, setCartQtyAction } from "@/app/[locale]/(shop)/cart/actions";
import { lineKey } from "@/components/cart/cart-keys";
import { useCart } from "@/components/cart/CartContext";
import { LineOptions } from "@/components/orders/LineOptions";
import { Icon } from "@/components/ui/Icon";
import { Link } from "@/i18n/navigation";
import { MAX_LINE_QTY } from "@/lib/commerce/cart-qty";
import { lineOptionRows } from "@/lib/commerce/line-options";
import { formatMoney } from "@/lib/currency-format";
import { pickLocale } from "@/lib/i18n-content";
import { cn } from "@/lib/utils";

/**
 * The cart panel that opens on a successful add (TRY-128 lineage: the flash used
 * to be a corner toast). Mounted once inside `CartProvider`, so it survives route
 * changes and shows the WHOLE cart, not just the line that was added.
 *
 * It is a column of the page, not an overlay: it lays `children` (the page) and
 * the panel out as one flex row, so opening it narrows the page at `md` and up,
 * and there it stays non-modal. Below `md` a side column would leave the page
 * unreadably thin, so there it covers the viewport and is modal: the page is
 * `inert` and the body does not scroll. Closed, the panel is `inert` and zero
 * width, so it takes no space and no focus.
 *
 * Below `md` it does NOT open on an add: covering the page after every tap
 * stops a shopper mid-browse. There the add button turns into the line's
 * quantity stepper in place, and the header's cart link leads to `/cart`.
 *
 * ⚠️ It reacts to `flash.kind === "added"` only. A `refused` flash is a view-only
 * store rejecting the add, and opening a cart drawer on it would show the customer
 * a cart their item is not in; `AddedToCartToast` still renders that case. An add
 * made with `announce: false` sets no flash at all, which is how Buy Now reaches
 * `/cart` without the drawer over it.
 *
 * ⚠️ The line controls mirror `CartView`'s: one `<form action>` per step with the
 * action passed DIRECTLY, never `useActionState`. The drawer needs JavaScript to
 * open at all, but keeping the same shape means the cookie has ONE writer and the
 * two surfaces cannot drift in what a step does.
 */
export function CartDrawer({
	children,
	currency,
	factor,
}: {
	children: ReactNode;
	currency: CurrencyInfo;
	factor: number;
}) {
	const t = useTranslations("shop");
	const tc = useTranslations("common");
	const locale = useLocale() as Locale;
	const { items, total, flash, setQty, remove } = useCart();
	const [open, setOpen] = useState(false);
	const lastFlashRef = useRef(0);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const returnFocusRef = useRef<HTMLElement | null>(null);
	const restoreFocusRef = useRef(false);
	const titleId = useId();
	const covers = useSyncExternalStore(subscribeNarrow, isNarrow, () => false);
	const modal = open && covers;

	useEffect(() => {
		if (!flash || flash.id === lastFlashRef.current) return;
		lastFlashRef.current = flash.id;
		if (flash.kind === "added" && !isNarrow()) {
			if (document.activeElement instanceof HTMLElement)
				returnFocusRef.current = document.activeElement;
			setOpen(true);
		}
	}, [flash]);

	/** Dismissal that stays on the page hands focus back to where the add was. */
	function close() {
		restoreFocusRef.current = true;
		setOpen(false);
	}

	// After commit, because the page is still `inert` while the cover is up.
	useEffect(() => {
		if (open || !restoreFocusRef.current) return;
		restoreFocusRef.current = false;
		const back = returnFocusRef.current;
		returnFocusRef.current = null;
		if (back?.isConnected) back.focus({ preventScroll: true });
	}, [open]);

	useEffect(() => {
		if (!open) return;
		// The panel is last in the DOM, so a keyboard user would otherwise have to
		// tab through the whole page to reach it. After commit, once `inert` is off.
		titleRef.current?.focus({ preventScroll: true });
		const onKey = (e: KeyboardEvent) => {
			// A layer above the panel that handled Escape closes first.
			if (e.key === "Escape" && !e.defaultPrevented) close();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open]);

	useEffect(() => {
		if (!modal) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [modal]);

	return (
		<div className="flex">
			{/* `min-w-0` lets the page shrink instead of overflowing. */}
			<div className="min-w-0 flex-1" inert={modal}>
				{children}
			</div>
			<aside
				aria-labelledby={titleId}
				inert={!open}
				className={cn(
					"z-40 shrink-0 overflow-hidden transition-[width] duration-300 ease-out motion-reduce:transition-none md:sticky md:top-0 md:h-dvh md:self-start",
					open ? "max-md:fixed max-md:inset-0 max-md:w-full md:w-80" : "max-md:hidden md:w-0",
				)}
			>
				<div className="flex h-full w-full flex-col border-s border-border bg-surface text-foreground md:w-80">
					<div className="flex items-center justify-between gap-2 border-b border-border p-4">
						<h2
							id={titleId}
							ref={titleRef}
							tabIndex={-1}
							className="text-lg font-bold outline-none"
						>
							{t("shoppingCart")}
						</h2>
						<button
							type="button"
							onClick={close}
							className="inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground"
						>
							<Icon icon={faXmark} size={14} />
							{t("close")}
						</button>
					</div>

					{items.length === 0 ? (
						<div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
							<div className="grid size-12 place-items-center rounded-full bg-surface-2 text-muted-foreground">
								<Icon icon={faCartShopping} size={18} />
							</div>
							<p className="text-sm text-muted-foreground">{t("emptyCart")}</p>
							<Link href="/" onClick={() => setOpen(false)} className="btn btn-outline text-sm">
								{t("continueShopping")}
							</Link>
						</div>
					) : (
						<ul className="flex-1 divide-y divide-border overflow-y-auto px-4">
							{items.map((i) => (
								<li key={lineKey(i)} className="flex gap-3 py-3">
									{i.image ? (
										// oxlint-disable-next-line nextjs/no-img-element -- cart line thumbnail, merchant-uploaded at arbitrary dimensions
										<img
											src={i.image}
											alt={pickLocale(i.name, locale)}
											className="h-14 w-14 shrink-0 rounded-lg object-cover"
										/>
									) : (
										<div className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-surface-2 text-base font-bold text-muted-foreground">
											{pickLocale(i.name, locale).slice(0, 1).toUpperCase()}
										</div>
									)}
									<div className="min-w-0 flex-1">
										<div className="flex items-start gap-2">
											<div className="min-w-0 flex-1">
												<p className="truncate text-sm font-medium">{pickLocale(i.name, locale)}</p>
												<LineOptions
													rows={lineOptionRows(i, locale)}
													locale={locale}
													labels={{ variant: tc("lineVariant"), addons: tc("lineAddons") }}
												/>
												{i.note && (
													<p className="truncate text-xs text-muted-foreground">
														<Icon icon={faNoteSticky} size={11} /> {i.note}
													</p>
												)}
												<p className="text-xs text-muted-foreground">
													{formatMoney(i.price * factor, currency)}
												</p>
											</div>
											{/*
												  A real `<form action>` like every other cart control
												  (TRY-128): the drawer needs JavaScript to open, but the
												  action stays the one path that writes the cookie.
												*/}
											<form
												action={removeCartLineAction}
												onSubmit={(e) => {
													e.preventDefault();
													remove(lineKey(i));
												}}
												className="shrink-0"
											>
												<input type="hidden" name="key" value={lineKey(i)} />
												<button
													type="submit"
													aria-label={`${t("remove")} ${pickLocale(i.name, locale)}`}
													className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-danger/10 hover:text-danger"
												>
													<Icon icon={faXmark} size={14} />
												</button>
											</form>
										</div>

										<div className="mt-2 flex items-center justify-between gap-2">
											<div className="flex items-center rounded-lg border border-border">
												{/* Stepping the last unit off a line is a removal, not an
													    error — `qtySchema` takes 0 — so the control says so
													    with the bin rather than sitting there inert. */}
												<form
													action={setCartQtyAction}
													onSubmit={(e) => {
														e.preventDefault();
														setQty(lineKey(i), i.qty - 1);
													}}
												>
													<input type="hidden" name="key" value={lineKey(i)} />
													<input type="hidden" name="qty" value={i.qty - 1} />
													<button
														type="submit"
														aria-label={i.qty <= 1 ? t("remove") : t("decreaseQty")}
														className="grid h-8 w-8 place-items-center text-muted-foreground transition hover:text-foreground"
													>
														<Icon icon={i.qty <= 1 ? faTrash : faMinus} size={11} />
													</button>
												</form>
												<span
													aria-label={t("quantity")}
													className="min-w-8 text-center text-sm font-semibold tabular-nums"
												>
													{i.qty}
												</span>
												<form
													action={setCartQtyAction}
													onSubmit={(e) => {
														e.preventDefault();
														setQty(lineKey(i), i.qty + 1);
													}}
												>
													<input type="hidden" name="key" value={lineKey(i)} />
													<input type="hidden" name="qty" value={i.qty + 1} />
													<button
														type="submit"
														aria-label={t("increaseQty")}
														disabled={i.qty >= MAX_LINE_QTY}
														className="grid h-8 w-8 place-items-center text-muted-foreground transition hover:text-foreground disabled:opacity-40"
													>
														<Icon icon={faPlus} size={11} />
													</button>
												</form>
											</div>
											<span className="shrink-0 text-sm font-semibold">
												{formatMoney(i.price * i.qty * factor, currency)}
											</span>
										</div>
									</div>
								</li>
							))}
						</ul>
					)}

					{/* Emptying the last line inside the drawer leaves it open, so the
				    footer goes with the cart — a subtotal over nothing and CTAs to
				    an empty cart page are dead ends at the other end of them. */}
					{items.length > 0 && (
						<div className="flex flex-col gap-2 border-t border-border p-4">
							<div className="flex items-center justify-between font-semibold">
								<span>{t("subtotal")}</span>
								<span>{formatMoney(total * factor, currency)}</span>
							</div>
							<Link
								href="/cart"
								onClick={() => setOpen(false)}
								className="btn btn-outline w-full text-sm"
							>
								{t("viewCart")}
							</Link>
							<Link
								href="/checkout"
								onClick={() => setOpen(false)}
								className="btn btn-primary w-full text-sm"
							>
								{t("checkout")}
							</Link>
						</div>
					)}
				</div>
			</aside>
		</div>
	);
}

/** Below Tailwind's `md`, where the open panel covers the page. */
const NARROW = "(width < 48rem)";

function subscribeNarrow(onChange: () => void) {
	const mq = window.matchMedia(NARROW);
	mq.addEventListener("change", onChange);
	return () => mq.removeEventListener("change", onChange);
}

function isNarrow() {
	return window.matchMedia(NARROW).matches;
}
